import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, take, timeout} from 'rxjs/operators';
import {LoginApiService} from '@shared/services/api/login-page/login-api.service';
import {NotifyService} from '@shared/services/global/notify.service';
import {AuthService} from './auth.service';
import {joinedPagesHaveBusinesses} from './page-business.utils';

/**
 * Kết thúc session Page nhưng vẫn giữ session tài khoản.
 *
 * Tương đương `PageService.outPages()` của FE1: dùng cho lỗi token Page/Zalo
 * khi gửi tin, không dùng `AuthService.logout()` để tránh đăng xuất toàn bộ user.
 */
@Injectable({providedIn: 'root'})
export class PageSessionService {
  private readonly loginApi = inject(LoginApiService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notifyService = inject(NotifyService);
  private isEnding = false;
  private endingPageSession$: Observable<boolean> | null = null;
  private hasVerifiedActivePageSession = false;
  private verifyActivePageSession$: Observable<boolean> | null = null;

  constructor() {
    this.authService.onAuthCleared$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.hasVerifiedActivePageSession = false;
      this.verifyActivePageSession$ = null;
      this.isEnding = false;
      this.endingPageSession$ = null;
    });
  }

  /**
   * `usr` chỉ là cache FE; session Page ở BE có thể mất sau khi refresh server
   * hoặc sau khi mở lại tab. Khôi phục nó bằng đúng API joinpages trước khi cho
   * vào khu vực hội thoại để BE không nhận `active page = null`.
   */
  public ensureActivePageSession(): Observable<boolean> {
    if (!this.authService.isLoginEd()) return of(false);
    if (this.hasVerifiedActivePageSession) {
      return of(true);
    }

    if (this.verifyActivePageSession$) {
      return this.verifyActivePageSession$;
    }

    const pageIds = this.authService.getPageIds()
      .map(pageId => String(pageId))
      .filter(Boolean);
    if (!pageIds.length) {
      return of(false);
    }

    const token = this.authService.getAuthorizationToken();
    this.verifyActivePageSession$ = this.loginApi.userJoinPages({ids: pageIds, affiliate: ''}).pipe(
      take(1),
      switchMap((response: any) => {
        if (!this.authService.isLoginEd() || token !== this.authService.getAuthorizationToken()) return of(false);
        const data = response?.data && typeof response.data === 'object'
          ? response.data
          : response;
        const code = Number(data?.code ?? response?.code);
        const restoredPageIds = Array.isArray(data?.pageIds)
          ? data.pageIds.map((pageId: string | number) => String(pageId)).filter(Boolean)
          : Array.isArray(data?.pages)
            ? data.pages.map((page: {id?: string | number}) => String(page?.id ?? '')).filter(Boolean)
            : [];

        if (code === 10 || code === 11 || (code === 1 && restoredPageIds.length && !joinedPagesHaveBusinesses(data))) {
          return this.rejectPagesWithoutBusiness();
        }

        if (code !== 1 || !restoredPageIds.length) {
          this.authService.outPages();
          return of(false);
        }

        this.authService.updateAuthorization(data);
        this.authService.isSelectPage = true;
        this.hasVerifiedActivePageSession = true;
        return of(true);
      }),
      catchError(() => {
        // Không giữ cache Page cũ khi BE không xác nhận được session hiện tại.
        this.authService.outPages();
        return of(false);
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );

    return this.verifyActivePageSession$;
  }

  /** Join page vừa hoàn tất trong phiên hiện tại, không cần gọi lại API. */
  public markActivePageSessionVerified(): void {
    if (!this.authService.isLoginEd()) return;
    this.hasVerifiedActivePageSession = true;
    this.verifyActivePageSession$ = null;
  }

  /** Chặn session V1 chưa gắn DN; giữ đăng nhập và chờ BE thoát Page trước khi điều hướng. */
  public rejectPagesWithoutBusiness(): Observable<boolean> {
    if (this.endingPageSession$) return this.endingPageSession$;
    if (!this.authService.isLoginEd()) return of(false);
    this.notifyService.warning('login.pageBusinessRequired');
    this.hasVerifiedActivePageSession = false;
    this.verifyActivePageSession$ = null;
    this.authService.outPages();
    this.endingPageSession$ = this.loginApi.logOutPage().pipe(
      take(1),
      timeout(15000),
      catchError(() => of(null)),
      map(() => false),
      finalize(() => { this.endingPageSession$ = null; }),
      shareReplay({bufferSize: 1, refCount: false})
    );
    return this.endingPageSession$;
  }

  public endPageSession(): void {
    if (this.isEnding || !this.authService.isLoginEd()) return;
    this.isEnding = true;

    this.loginApi.logOutPage()
      .pipe(
        take(1),
        // Dù BE phản hồi lỗi vẫn phải xoá state Page local để không kẹt màn hình chat.
        finalize(() => this.finish())
      )
      .subscribe({error: () => undefined});
  }

  private finish(): void {
    this.isEnding = false;
    this.hasVerifiedActivePageSession = false;
    this.verifyActivePageSession$ = null;
    if (!this.authService.isLoginEd()) return;
    this.authService.outPages();
    void this.router.navigate(['/login']);
  }
}
